import { View, Button, Input } from "@/components/ui";
import { Description } from "@/components/shared/description";
import {
  SealPercent,
  MapPinLine,
  CalendarDots,
  Receipt,
} from "@/components/shared/icons";
import {
  AccordionContent,
  AccordionTrigger,
  AccordionItem,
  Accordion,
} from "@/components/ui/accordion";
import { ListHeading } from "@/components/shared/list-header";
import { BookingPayLater } from "@/components/shared/booking/pay-later";
import { AgreeTerms } from "../agree-terms";
import { BookingCountDown } from "@/containers/booking-info/booking-count-down";
import type { BookingInfoFormData } from "@/containers/booking-info/useBookingInfoForm";
import type { UseFormReturn } from "react-hook-form";
import type { PromoCodeFormData } from "..";
import { InputFormControl } from "@/components/shared/input-form-control";
import { OrderDetails } from "@/components/shared/booking/order-card/order-details";
import { Typography } from "@/components/shared/typography";

export const BookingConfirmationMain = ({
  name,
  phoneNumber,
  email,
  fullAddress,
  accessMode,
  accessInstructions,
  note,
  serviceDate,
  frequencyAndTime,
  promoCodeFormReturn,
  promoCode,
  promoCodeLoading,
  onApplyPromoCode,
  onDeletePromoCode,
}: {
  promoCodeLoading: boolean;
  serviceDate: string;
  frequencyAndTime: string;
  promoCodeFormReturn: UseFormReturn<PromoCodeFormData>;
  promoCode?: string;
  onApplyPromoCode(): void;
  onDeletePromoCode(): void;
} & BookingInfoFormData) => {
  return (
    <View>
      <View className="flex flex-col items-start gap-4 border-secondary-border p-4 pt-2">
        <BookingCountDown className="w-full" />
      </View>

      <View className="mx-4 border-b-2 border-secondary-border pb-4">
        <BookingPayLater />
      </View>

      <View className="border-b-2 border-secondary-border px-4 pb-0 pt-4 md:border-b-0">
        <ListHeading title="Booking Timing" icon={CalendarDots} />
        <Description title="Service date" description={serviceDate} />
        <Description title="Frequency & Time" description={frequencyAndTime} />
        <Description
          showLineOnSmallScreen={false}
          title="Additional Note"
          description={note || "-"}
        />
      </View>

      <View className="border-b-2 border-secondary-border p-4 py-4 md:border-b-0">
        <ListHeading title="Contact and Address Info" icon={MapPinLine} />
        <Description title="Contact Name" description={name} />
        <View className="flex flex-col gap-1">
          <Description title="Email" description={email} />
          <Description title="Phone" description={phoneNumber} />
        </View>
        <Description title="Address" description={fullAddress} />
        {/* 
          <Description title="Unit Number" description={unitNumber || "-"} /> 
          -- We implement unitNumber in the address on booking info 
        */}
        <Description title="Access Mode" description={accessMode} />
        <Description
          showLineOnSmallScreen={false}
          title="Access Instructions"
          description={accessInstructions || "-"}
        />
      </View>

      <View className="border-b-2 border-secondary-border px-4 py-2 md:border-b-0">
        <Accordion type="single" collapsible>
          <AccordionItem value="promoCode" className="border-b-0">
            <AccordionTrigger className="p-0 hover:no-underline">
              <ListHeading title="Promo Code" icon={SealPercent} />
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              <View className="flex flex-col gap-2">
                <Typography className="text-sm text-secondary-text">
                  One promo code per transaction
                </Typography>
                <InputFormControl
                  name="promoCode"
                  control={promoCodeFormReturn.control}
                  component={Input}
                  componentProps={{
                    placeholder: "Enter Promo Code",
                    disabled: !!promoCode,
                  }}
                  rightRender={
                    promoCode ? (
                      <Button
                        variant="primary"
                        color="danger"
                        onClick={onDeletePromoCode}
                        children="Delete"
                      />
                    ) : (
                      <Button
                        variant="secondary"
                        color="CTA2"
                        onClick={onApplyPromoCode}
                        loading={promoCodeLoading}
                        children="Apply"
                      />
                    )
                  }
                />
              </View>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </View>

      <View className="border-b-2 border-secondary-border p-4 py-4 md:hidden md:border-b-0">
        <ListHeading title="Your Order" icon={Receipt} />
        <View>
          <OrderDetails showTotal showTotalDivider />
        </View>
      </View>

      <View className="border-b-2 border-secondary-border p-4 py-4 md:hidden md:border-b-0">
        <AgreeTerms />
      </View>
    </View>
  );
};
